import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaHourglassHalf,
  FaHome,
  FaHeart,
  FaDownload,
  FaEnvelope,
  FaShare,
  FaWhatsapp,
  FaFacebookF,
  FaTwitter
} from 'react-icons/fa';
import { checkPaymentStatus, formatAmount } from '../services/phonepeService';
import { 
  checkPaymentStatus as checkPaymentStatusDirect, 
  formatAmount as formatAmountDirect,
  getPaymentStatusFromURL,
  handlePaymentSuccess
} from '../services/phonepeDirectService';
import './DonationSuccessPage.css';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.5 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1
    }
  }
};

const DonationSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State
  const [paymentStatus, setPaymentStatus] = useState('checking'); // checking, success, failed, pending
  const [paymentData, setPaymentData] = useState(null);
  const [donationDetails, setDonationDetails] = useState(null);
  const [error, setError] = useState(null);

  // Get transaction ID from URL
  const transactionId = searchParams.get('transactionId');

  // Check payment status on component mount
  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get donation details from session storage
        const storedDetails = sessionStorage.getItem('donationDetails');
        if (storedDetails) {
          setDonationDetails(JSON.parse(storedDetails));
        }

        // First try to get status from URL parameters (for direct integration)
        const urlStatus = getPaymentStatusFromURL();
        if (urlStatus.hasTransactionId) {
          console.log('Payment status from URL:', urlStatus);
          
          if (urlStatus.status === 'COMPLETED' || urlStatus.status === 'SUCCESS') {
            setPaymentStatus('success');
            setPaymentData({
              state: urlStatus.status,
              amount: urlStatus.amount ? urlStatus.amount * 100 : 0, // Convert to paise
              transactionId: urlStatus.transactionId
            });
            return;
          }
        }

        if (!transactionId) {
          setPaymentStatus('failed');
          setError('Transaction ID not found');
          return;
        }

        // Check payment status using direct service
        console.log('Checking payment status (Direct) for:', transactionId);
        const statusResponse = await checkPaymentStatusDirect(transactionId);
        
        console.log('Payment status response (Direct):', statusResponse);
        setPaymentData(statusResponse.data);

        // Set status based on response
        const state = statusResponse.status?.toUpperCase();
        if (state === 'COMPLETED' || state === 'SUCCESS') {
          setPaymentStatus('success');
        } else if (state === 'FAILED' || state === 'CANCELLED') {
          setPaymentStatus('failed');
        } else if (state === 'PENDING') {
          setPaymentStatus('pending');
        } else {
          setPaymentStatus('failed');
          setError('Unknown payment status');
        }
      } catch (err) {
        console.error('Error verifying payment:', err);
        setPaymentStatus('failed');
        setError(err.message || 'Failed to verify payment');
      }
    };

    verifyPayment();
  }, [transactionId]);

  // Handle download receipt
  const handleDownloadReceipt = () => {
    // In a real application, this would generate a proper receipt PDF
    alert('Receipt download feature will be implemented soon. You will receive a receipt via email.');
  };

  // Handle share
  const handleShare = (platform) => {
    const message = encodeURIComponent(`I just donated to Ummeed Ki Kiran Foundation Trust! Join me in making a difference. ${window.location.origin}/donate`);
    const urls = {
      whatsapp: `https://wa.me/?text=${message}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin + '/donate')}`,
      twitter: `https://twitter.com/intent/tweet?text=${message}`
    };
    
    if (urls[platform]) {
      window.open(urls[platform], '_blank');
    }
  };

  // Render checking state
  if (paymentStatus === 'checking') {
    return (
      <div className="donation-success-page checking">
        <div className="checking-container">
          <motion.div
            className="checking-icon"
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          >
            <FaHourglassHalf />
          </motion.div>
          <h2>Verifying Your Payment...</h2>
          <p>Please wait while we confirm your donation</p>
        </div>
      </div>
    );
  }

  // Render success state
  if (paymentStatus === 'success') {
    return (
      <div className="donation-success-page success">
        <div className="container">
          <motion.div
            className="success-content"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {/* Success Icon */}
            <motion.div className="status-icon success-icon" variants={scaleIn}>
              <FaCheckCircle />
            </motion.div>

            {/* Success Message */}
            <motion.h1 className="success-title" variants={fadeInUp}>
              Thank You for Your Donation!
            </motion.h1>
            <motion.p className="success-subtitle" variants={fadeInUp}>
              Your generous contribution will help transform lives
            </motion.p>

            {/* Donation Details Card */}
            <motion.div className="details-card" variants={fadeInUp}>
              <h2 className="details-title">Donation Details</h2>
              
              <div className="detail-row">
                <span className="detail-label">Transaction ID:</span>
                <span className="detail-value transaction-id">{transactionId}</span>
              </div>

              {donationDetails && (
                <>
                  <div className="detail-row">
                    <span className="detail-label">Donor Name:</span>
                    <span className="detail-value">{donationDetails.name}</span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{donationDetails.email}</span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Donation Type:</span>
                    <span className="detail-value">
                      {donationDetails.donationType === 'one-time' ? 'One-Time' : 'Monthly'}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Purpose:</span>
                    <span className="detail-value">
                      {donationDetails.purpose.charAt(0).toUpperCase() + donationDetails.purpose.slice(1).replace('-', ' ')}
                    </span>
                  </div>
                </>
              )}

              <div className="detail-row amount-row">
                <span className="detail-label">Amount:</span>
                <span className="detail-value amount-value">
                  {paymentData?.amount 
                    ? formatAmountDirect(paymentData.amount / 100) 
                    : donationDetails?.amount 
                    ? formatAmountDirect(donationDetails.amount)
                    : 'N/A'}
                </span>
              </div>

              <div className="tax-info">
                <FaEnvelope className="tax-icon" />
                <p>A tax receipt will be sent to your email shortly</p>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div className="action-buttons" variants={fadeInUp}>
              <button className="action-button primary" onClick={handleDownloadReceipt}>
                <FaDownload />
                Download Receipt
              </button>
              <button className="action-button secondary" onClick={() => navigate('/')}>
                <FaHome />
                Back to Home
              </button>
            </motion.div>

            {/* Share Section */}
            <motion.div className="share-section" variants={fadeInUp}>
              <h3 className="share-title">
                <FaHeart className="share-heart" />
                Spread the Word
              </h3>
              <p className="share-subtitle">Inspire others to make a difference</p>
              <div className="share-buttons">
                <button 
                  className="share-button whatsapp" 
                  onClick={() => handleShare('whatsapp')}
                  title="Share on WhatsApp"
                >
                  <FaWhatsapp />
                </button>
                <button 
                  className="share-button facebook" 
                  onClick={() => handleShare('facebook')}
                  title="Share on Facebook"
                >
                  <FaFacebookF />
                </button>
                <button 
                  className="share-button twitter" 
                  onClick={() => handleShare('twitter')}
                  title="Share on Twitter"
                >
                  <FaTwitter />
                </button>
              </div>
            </motion.div>

            {/* Next Steps */}
            <motion.div className="next-steps-card" variants={fadeInUp}>
              <h3>What Happens Next?</h3>
              <div className="steps-list">
                <div className="step-item">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>Email Confirmation</h4>
                    <p>You'll receive a confirmation email with your donation receipt</p>
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>Tax Certificate</h4>
                    <p>Your 80G tax exemption certificate will be emailed within 7 working days</p>
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>Impact Updates</h4>
                    <p>We'll keep you updated on how your donation is making a difference</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Thank You Message */}
            <motion.div className="thank-you-message" variants={fadeInUp}>
              <p>
                Your support means the world to us and the children we serve. 
                Together, we're building brighter futures and creating lasting change in communities.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Render pending state
  if (paymentStatus === 'pending') {
    return (
      <div className="donation-success-page pending">
        <div className="container">
          <motion.div
            className="status-content"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div className="status-icon pending-icon" variants={scaleIn}>
              <FaHourglassHalf />
            </motion.div>

            <motion.h1 className="status-title" variants={fadeInUp}>
              Payment Pending
            </motion.h1>
            <motion.p className="status-subtitle" variants={fadeInUp}>
              Your payment is being processed
            </motion.p>

            <motion.div className="info-card" variants={fadeInUp}>
              <p>
                Your payment is currently being processed by the payment gateway. 
                This usually takes a few minutes.
              </p>
              <p className="transaction-ref">
                Transaction ID: <strong>{transactionId}</strong>
              </p>
              <p>
                We'll send you an email confirmation once the payment is completed.
                If you don't receive it within 30 minutes, please contact us.
              </p>
            </motion.div>

            <motion.div className="action-buttons" variants={fadeInUp}>
              <button 
                className="action-button primary" 
                onClick={() => window.location.reload()}
              >
                Check Status Again
              </button>
              <button 
                className="action-button secondary" 
                onClick={() => navigate('/')}
              >
                <FaHome />
                Back to Home
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Render failed state
  return (
    <div className="donation-success-page failed">
      <div className="container">
        <motion.div
          className="status-content"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.div className="status-icon failed-icon" variants={scaleIn}>
            <FaTimesCircle />
          </motion.div>

          <motion.h1 className="status-title" variants={fadeInUp}>
            Payment Failed
          </motion.h1>
          <motion.p className="status-subtitle" variants={fadeInUp}>
            {error || 'Something went wrong with your payment'}
          </motion.p>

          <motion.div className="info-card" variants={fadeInUp}>
            <p>
              Unfortunately, your payment could not be processed. 
              This could be due to:
            </p>
            <ul className="error-reasons">
              <li>Insufficient funds in your account</li>
              <li>Network connectivity issues</li>
              <li>Payment gateway timeout</li>
              <li>Transaction was cancelled</li>
            </ul>
            {transactionId && (
              <p className="transaction-ref">
                Transaction ID: <strong>{transactionId}</strong>
              </p>
            )}
            <p>
              Don't worry, no money has been deducted from your account. 
              Please try again or contact us if you continue to face issues.
            </p>
          </motion.div>

          <motion.div className="action-buttons" variants={fadeInUp}>
            <button 
              className="action-button primary" 
              onClick={() => navigate('/donate')}
            >
              <FaHeart />
              Try Again
            </button>
            <button 
              className="action-button secondary" 
              onClick={() => navigate('/')}
            >
              <FaHome />
              Back to Home
            </button>
          </motion.div>

          <motion.div className="contact-info" variants={fadeInUp}>
            <p>Need help? Contact us at:</p>
            <a href="mailto:support@ummeedkikiran.org">support@ummeedkikiran.org</a>
            <span> or call </span>
            <a href="tel:+917985947303">+91 7985947303</a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default DonationSuccessPage;

